import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { GitBranch, ChevronDown, ChevronUp, Building2, Users, AlertTriangle } from 'lucide-react';
import config from '../../config';
import { jwtDecode } from 'jwt-decode';

const BranchesDashboard = () => {
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [expandedBranches, setExpandedBranches] = useState({});
  const [departmentsByBranch, setDepartmentsByBranch] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [allDepartments, setAllDepartments] = useState([]);

  // Decode token and fetch companies
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        console.log('🔍 Decoded token:', decoded);
        const userCompanyId = localStorage.getItem('selectedCompanyId') || decoded.companyId || decoded.company || decoded.id;
        setCompanyId(userCompanyId);
        setSelectedCompanyId(userCompanyId);
        if (userCompanyId) localStorage.setItem('selectedCompanyId', userCompanyId);
      } catch (err) {
        console.error('Error decoding token:', err);
        setError('Invalid authentication token');
      }
    } else {
      setError('No authentication token found');
    }
  }, []);

  // Fetch companies when token is available
  useEffect(() => {
    if (companyId) {
      fetchCompanies();
    }
  }, [companyId]);

  // Fetch branches and departments when company is selected
  useEffect(() => {
    if (selectedCompanyId) {
      fetchBranches(selectedCompanyId);
      fetchAllDepartments(selectedCompanyId); // Now passing company ID
    }
  }, [selectedCompanyId]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem("authToken");

      // First try to get the specific company from the token
      if (companyId) {
        const response = await fetch(`https://greenyellow-nightingale-241896.hostingersite.com/api/companies/${companyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const companyData = await response.json();
          console.log('🏢 Single company data:', companyData);
          setCompanies([companyData]);
          return;
        }
      }

      // If single company fetch fails, try to get all companies
      const allCompaniesResponse = await fetch('https://greenyellow-nightingale-241896.hostingersite.com/api/companies', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (allCompaniesResponse.ok) {
        const allCompaniesData = await allCompaniesResponse.json();
        console.log('🏢 All companies data:', allCompaniesData);

        let companiesArray = [];
        if (Array.isArray(allCompaniesData)) {
          companiesArray = allCompaniesData;
        } else if (allCompaniesData.data && Array.isArray(allCompaniesData.data)) {
          companiesArray = allCompaniesData.data;
        } else if (allCompaniesData.companies && Array.isArray(allCompaniesData.companies)) {
          companiesArray = allCompaniesData.companies;
        }

        setCompanies(companiesArray);
      }
    } catch (err) {
      console.error('❌ Error fetching companies:', err);
      setError('Failed to fetch companies');
    }
  };

  const fetchBranches = async (targetCompanyId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      console.log('📋 Fetching branches for company:', targetCompanyId);

      // Try different endpoints for fetching branches
      let branchesData = [];

      // Option 1: Company-specific branches endpoint
      try {
        const response = await fetch(`https://greenyellow-nightingale-241896.hostingersite.com/api/branches/company/${targetCompanyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('📍 Company branches response:', data);

          if (Array.isArray(data)) {
            branchesData = data;
          } else if (data.data && Array.isArray(data.data)) {
            branchesData = data.data;
          } else if (data.branches && Array.isArray(data.branches)) {
            branchesData = data.branches;
          }
        }
      } catch (err) {
        console.log('Company branches endpoint failed, trying general endpoint');
      }

      // Option 2: General branches endpoint with filtering
      if (branchesData.length === 0) {
        try {
          const response = await fetch('https://greenyellow-nightingale-241896.hostingersite.com/api/branches/company/' + targetCompanyId, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          });

          if (response.ok) {
            const data = await response.json();
            console.log('📍 All branches response:', data);

            let allBranches = [];
            if (Array.isArray(data)) {
              allBranches = data;
            } else if (data.data && Array.isArray(data.data)) {
              allBranches = data.data;
            } else if (data.branches && Array.isArray(data.branches)) {
              allBranches = data.branches;
            }

            // Filter branches by company ID
            branchesData = allBranches.filter(branch =>
              branch.company === targetCompanyId ||
              branch.companyId === targetCompanyId ||
              branch.parentCompanyId === targetCompanyId
            );
          }
        } catch (err) {
          console.log('General branches endpoint also failed');
        }
      }

      console.log('✅ Final branches data:', branchesData);
      setBranches(branchesData);
      setError(null);
    } catch (err) {
      console.error('❌ Error fetching branches:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDepartments = async (targetCompanyId) => {
    try {
      const token = localStorage.getItem("authToken");

      console.log('🏢 Fetching all departments for company:', targetCompanyId);

      const response = await fetch(`https://greenyellow-nightingale-241896.hostingersite.com/api/departments/company/${targetCompanyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        console.warn('⚠️ Failed to fetch departments for company:', targetCompanyId);
        return;
      }

      const data = await response.json();
      const departments = Array.isArray(data) ? data :
        data.data && Array.isArray(data.data) ? data.data :
          data.departments && Array.isArray(data.departments) ? data.departments : [];

      console.log('✅ Fetched departments:', departments.length);
      setAllDepartments(departments);

      // If we already have branches, assign them
      if (branches.length > 0) {
        assignDepartmentsToBranches(departments, branches);
      }

    } catch (err) {
      console.error("❌ Error fetching departments:", err);
      setAllDepartments([]);
    }
  };



  const assignDepartmentsToBranches = (departments, branchesList) => {
    if (!Array.isArray(branchesList)) {
      console.warn('⚠️ branchesList is not an array in assignDepartmentsToBranches');
      return;
    }

    const departmentsByBranchMap = {};

    branchesList.forEach(branch => {
      const branchId = branch._id || branch.id;

      // Since the current schema links departments to companies, we show all company 
      // departments for each branch, OR we filter if departments have a branch field.
      // For now, we'll show all company departments as they are accessible from any branch.
      // If departments have a 'branch' field, use: dept.branch === branchId
      const branchDepartments = departments.filter(dept =>
        dept.branch === branchId || dept.branchId === branchId || dept.company === branchId
      );

      departmentsByBranchMap[branchId] = branchDepartments;
    });

    setDepartmentsByBranch(departmentsByBranchMap);
  };




  // Update departments assignment when branches or departments change
  useEffect(() => {
    if (branches.length > 0 && allDepartments.length > 0) {
      assignDepartmentsToBranches(allDepartments, branches);
    }
  }, [branches, allDepartments]);


  const fetchDepartmentsForBranch = async (branchId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`https://greenyellow-nightingale-241896.hostingersite.com/api/departments/branch/${branchId}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch departments for branch ${branchId}`);
      }

      const data = await response.json();
      let departmentsData = [];

      if (Array.isArray(data)) {
        departmentsData = data;
      } else if (data.data && Array.isArray(data.data)) {
        departmentsData = data.data;
      } else if (data.departments && Array.isArray(data.departments)) {
        departmentsData = data.departments;
      }

      return departmentsData;
    } catch (err) {
      console.error("❌ Error fetching branch departments:", err);
      return [];
    }
  };



  // const toggleBranch = async (branchId) => {
  //   setExpandedBranches((prev) => ({
  //     ...prev,
  //     [branchId]: !prev[branchId],
  //   }));

  //   // Fetch departments only once per branch
  //   if (!departmentsByBranch[branchId]) {
  //     try {
  //       const token = localStorage.getItem("authToken");
  //       const response = await fetch(`https://greenyellow-nightingale-241896.hostingersite.com/api/departments/branch/${branchId}`, {
  //         headers: {
  //           "Authorization": `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //       });

  //       if (!response.ok) {
  //         throw new Error("Failed to fetch branch departments");
  //       }

  //       const data = await response.json();
  //       const departments = Array.isArray(data) ? data : data.data || [];

  //       setDepartmentsByBranch((prev) => ({
  //         ...prev,
  //         [branchId]: departments,
  //       }));
  //     } catch (err) {
  //       console.error("❌ Error fetching branch departments:", err);
  //     }
  //   }
  // };



  const handleCompanyChange = (e) => {
    const newCompanyId = e.target.value;
    setSelectedCompanyId(newCompanyId);
    setBranches([]); // Clear branches when company changes
    setDepartmentsByBranch({}); // Clear departments cache
    setExpandedBranches({}); // Clear expanded state
    setAllDepartments([]); // Clear all departments
  };

  const calculateStats = () => {
    const totalDepartments = Object.values(departmentsByBranch).reduce(
      (sum, depts) => sum + (depts?.length || 0), 0
    );

    return {
      totalBranches: branches.length,
      totalDepartments,
      expandedCount: Object.values(expandedBranches).filter(Boolean).length,
      totalCompanyDepartments: allDepartments.length
    };
  };

  const stats = calculateStats();

  if (loading && branches.length === 0) {
    return (
      <div className="min-h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading branches dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-red-800 mb-2">Error Loading Dashboard</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
        >
          Reload Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold ml-4 text-gray-900">  Branches </h1>
          <p className="text-gray-600 mt-2">Manage your organization structure</p>
        </div>

        {/* Company Selector */}
        {companies.length > 0 && (
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-gray-700">Company:</label>
            <select
              value={selectedCompanyId}
              onChange={handleCompanyChange}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {companies.map(company => (
                <option key={company._id} value={company._id}>
                  {company.businessName || company.name || 'Unnamed Company'}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Branches</p>
              <h3 className="text-3xl font-bold mt-1">{stats.totalBranches}</h3>
            </div>
            <Building2 className="w-12 h-12 text-blue-200 opacity-80" />
          </div>
        </div>

        {/* <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-sm p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Total Departments</p>
              <h3 className="text-3xl font-bold mt-1">{stats.totalDepartments}</h3>
            </div>
            <GitBranch className="w-12 h-12 text-green-200 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-sm p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Expanded Branches</p>
              <h3 className="text-3xl font-bold mt-1">{stats.expandedCount}</h3>
            </div>
            <ChevronDown className="w-12 h-12 text-purple-200 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-sm p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Company Departments</p>
              <h3 className="text-3xl font-bold mt-1">{stats.totalCompanyDepartments}</h3>
            </div>
            <Users className="w-12 h-12 text-orange-200 opacity-80" />
          </div>
        </div> */}
      </div>

      {/* Debug Info */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <div className="font-medium mb-2">📊 Data Summary:</div>
        <div>Branches: {branches.length}</div>


      </div>

      {/* Branches List with Toggle */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Branches Overview {selectedCompanyId && `- ${companies.find(c => c._id === selectedCompanyId)?.businessName || 'Selected Company'}`}
          </h2>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <GitBranch className="w-4 h-4" />
            <span>{branches.length} Active Branches</span>
          </div>
        </div>

        {branches.length > 0 ? (
          <div className="space-y-3">
            {branches.map((branch) => {
              const branchId = branch._id || branch.id;
              const branchCompanyId = branch.company || branch.companyId || selectedCompanyId;
              const isExpanded = expandedBranches[branchId];
              const departments = departmentsByBranch[branchId] || [];

              return (
                <div key={branchId} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  {/* Branch Header - Clickable */}
                  <button
                    // onClick={() => toggleBranch(branchId, branchCompanyId)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-900">
                          {branch.businessName || branch.name || branch.branchName || 'Unnamed Branch'}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {branch.address || branch.location || 'No location specified'}
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-gray-400 mt-1">
                          <span>Email: {branch.businessEmail || branch.email || 'No email'}</span>
                          <span>Company ID: {branchCompanyId}</span>
                        </div>
                      </div>
                    </div>

                    {/* <div className="flex items-center space-x-3">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {departments.length} Department{departments.length !== 1 ? 's' : ''}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div> */}


                  </button>

                  {/* Departments - Shown when expanded */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      {departments.length > 0 ? (
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-semibold text-gray-900">Departments</h4>
                            <span className="text-sm text-gray-500">
                              Showing {departments.length} department{departments.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {departments.map((dept) => (
                              <div
                                key={dept._id || dept.id}
                                className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-all hover:border-blue-300"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                      <h4 className="font-medium text-gray-900">
                                        {dept.name || dept.departmentName || 'Unnamed Department'}
                                      </h4>
                                    </div>
                                    <div className="space-y-1 text-sm text-gray-500">
                                      <div>ID: {dept._id}</div>
                                      <div>Company: {dept.company || dept.companyId}</div>
                                      {dept.createdAt && (
                                        <div>Created: {new Date(dept.createdAt).toLocaleDateString()}</div>
                                      )}
                                    </div>
                                    {dept.description && (
                                      <p className="text-xs text-gray-500 mt-2">{dept.description}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <GitBranch className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="text-gray-500">No departments found for this branch</p>
                          <p className="text-sm text-gray-400 mt-1">
                            Departments are linked to the company. This branch shares departments with other branches in the same company.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Branches Found</h3>
            <p className="text-gray-500 mb-4">
              {selectedCompanyId
                ? `There are no branches available for the selected company`
                : 'Please select a company to view branches'
              }
            </p>
          </div>
        )}
      </div>

      {/* Department Statistics Chart */}
      {stats.totalDepartments > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Distribution by Branch</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={branches.map(branch => ({
              name: branch.businessName || branch.name || branch.branchName || 'Branch',
              departments: departmentsByBranch[branch._id || branch.id]?.length || 0
            })).filter(item => item.departments > 0)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="departments" fill="#3b82f6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default BranchesDashboard;

